import { Fragment, useEffect, useState, type CSSProperties, type FC } from 'react';
import type {
  TeacherAttendanceRecord,
  TeacherDiscipline,
  TeacherGradeRecord,
  TeacherGroup,
  TeacherProfile,
  TeacherStudent,
} from '../types';
import { BookOpenIcon, CheckIcon, ClockIcon, XIcon } from './TeacherIcons';

interface TeacherGradebookOverlayProps {
  disciplines: TeacherDiscipline[];
  selectedDisciplineCode: string;
  onSelectDiscipline: (code: string) => void;
  groups: TeacherGroup[];
  selectedGroupCode: string;
  onSelectGroup: (code: string) => void;
  students: TeacherStudent[];
  initialGrades: TeacherGradeRecord[];
  initialAttendance: TeacherAttendanceRecord[];
  profile?: TeacherProfile | null;
  onBackToCabinet: () => void;
  onOpenCourseWorkspace: () => void;
  onSaveGrades: (grades: TeacherGradeRecord[]) => Promise<void>;
  onSaveAttendance: (records: TeacherAttendanceRecord[]) => Promise<void>;
}

type ModeTab = 'scores' | 'attendance';

export const TeacherGradebookOverlay: FC<TeacherGradebookOverlayProps> = ({
  disciplines,
  selectedDisciplineCode,
  onSelectDiscipline,
  groups,
  selectedGroupCode,
  onSelectGroup,
  students,
  initialGrades,
  initialAttendance,
  profile,
  onBackToCabinet,
  onOpenCourseWorkspace,
  onSaveGrades,
  onSaveAttendance,
}) => {
  const [saveError, setSaveError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [activeMode, setActiveMode] = useState<ModeTab>('scores');
  const [selectedWeek, setSelectedWeek] = useState<number>(5);
  const [localGrades, setLocalGrades] = useState<TeacherGradeRecord[]>(initialGrades);
  const [localAttendance, setLocalAttendance] = useState<TeacherAttendanceRecord[]>(initialAttendance);
  const [selectedStudentForModal, setSelectedStudentForModal] = useState<TeacherStudent | null>(null);

  useEffect(() => {
    setLocalGrades(initialGrades);
  }, [initialGrades]);

  useEffect(() => {
    setLocalAttendance(initialAttendance);
  }, [initialAttendance]);

  const currentDiscipline = disciplines.find((d) => d.code === selectedDisciplineCode) || disciplines[0];

  const handleGradeCellChange = (
    studentName: string,
    weekNum: number,
    field: 'lecture_score' | 'practice_score' | 'sro_score',
    val: number
  ) => {
    setLocalGrades((prev) => {
      const copy = [...prev];
      const idx = copy.findIndex(
        (g) =>
          (g.student_user_id || g.student_name) === studentName &&
          g.week_number === weekNum &&
          g.discipline_code === selectedDisciplineCode &&
          g.group_code === selectedGroupCode
      );
      if (idx !== -1) {
        copy[idx] = { ...copy[idx], [field]: val };
      } else {
        copy.push({
          id: `gr-${Date.now()}-${Math.random()}`,
          discipline_code: selectedDisciplineCode,
          group_code: selectedGroupCode,
          student_name: students.find(s => (s.user_id || s.full_name) === studentName)?.full_name || studentName,
          student_user_id: students.find(s => s.user_id === studentName)?.user_id,
          week_number: weekNum,
          lecture_score: field === 'lecture_score' ? val : 0,
          practice_score: field === 'practice_score' ? val : 0,
          sro_score: field === 'sro_score' ? val : 0,
        });
      }
      return copy;
    });
  };

  const getScoreValue = (
    studentName: string,
    weekNum: number,
    field: 'lecture_score' | 'practice_score' | 'sro_score'
  ): number => {
    const item = localGrades.find(
      (g) =>
        (g.student_user_id || g.student_name) === studentName &&
        g.week_number === weekNum &&
        g.discipline_code === selectedDisciplineCode &&
        g.group_code === selectedGroupCode
    );
    if (item) return item[field];
    return 0;
  };

  const cycleAttendanceStatus = (currentStatus: string): string => {
    if (currentStatus === 'Присутствует') return 'Отсутствует';
    if (currentStatus === 'Отсутствует') return 'Опоздал';
    return 'Присутствует';
  };

  const handleAttendanceClick = (studentName: string, lessonType: 'LECTURE' | 'PRACTICE') => {
    setLocalAttendance((prev) => {
      const copy = [...prev];
      const idx = copy.findIndex(
        (a) =>
          (a.student_user_id || a.student_name) === studentName &&
          a.week_number === selectedWeek &&
          a.lesson_type === lessonType &&
          a.discipline_code === selectedDisciplineCode &&
          a.group_code === selectedGroupCode
      );
      if (idx !== -1) {
        const nextStatus = cycleAttendanceStatus(copy[idx].status);
        copy[idx] = { ...copy[idx], status: nextStatus };
      } else {
        copy.push({
          id: `att-${Date.now()}-${Math.random()}`,
          discipline_code: selectedDisciplineCode,
          group_code: selectedGroupCode,
          student_name: students.find(s => (s.user_id || s.full_name) === studentName)?.full_name || studentName,
          student_user_id: students.find(s => s.user_id === studentName)?.user_id,
          week_number: selectedWeek,
          lesson_type: lessonType,
          status: 'Присутствует',
        });
      }
      return copy;
    });
  };

  const getAttendanceStatus = (studentName: string, lessonType: 'LECTURE' | 'PRACTICE'): string => {
    const rec = localAttendance.find(
      (a) =>
        (a.student_user_id || a.student_name) === studentName &&
        a.week_number === selectedWeek &&
        a.lesson_type === lessonType &&
        a.discipline_code === selectedDisciplineCode &&
        a.group_code === selectedGroupCode
    );
    return rec ? rec.status : 'Не отмечено';
  };

  const handleSaveCurrentMode = async () => {
    if (isSaving) return;
    setIsSaving(true); setSaveError('');
    try { if (activeMode === 'scores') await onSaveGrades(localGrades); else await onSaveAttendance(localAttendance); }
    catch (e) { setSaveError(e instanceof Error ? e.message : 'Не удалось сохранить журнал'); }
    finally { setIsSaving(false); }
  };

  const statusIcon = (status: string) => {
    if (status === 'Присутствует') return <CheckIcon className="w-3.5 h-3.5" />;
    if (status === 'Отсутствует') return <XIcon className="w-3.5 h-3.5" />;
    return <ClockIcon className="w-3.5 h-3.5" />;
  };

  return (
    <div className="cab-shell cab-shell--wide">
      <div className="cab-top">
        <div className="cab-top-left">
          <div>
            <small>Кабинет преподавателя</small>
            <h2>Электронный журнал</h2>
            <p>Дисциплина → группа → пять пройденных недель → посещаемость и баллы по видам работы.</p>
          </div>
        </div>
        <div className="cab-top-actions">
          <button type="button" className="cab-btn" onClick={onOpenCourseWorkspace}>
            <BookOpenIcon className="w-4 h-4" />
            Учебный процесс
          </button>
          <button type="button" className="cab-btn primary" onClick={handleSaveCurrentMode}>
            Сохранить
          </button>
          <button type="button" className="cab-btn" onClick={onBackToCabinet}>
            В кабинет
          </button>
        </div>
      </div>

      <div className="cab-body">
        {saveError && <p role="alert">{saveError}</p>}
        <p className="text-xs">Ручные оценки задаются в процентах (0–100). Баллы опубликованных заданий изменяются в проверке работ.</p>
        <div className="cabinet-note" style={{ marginBottom: 14 }}>
          {profile?.full_name || '—'} · {profile?.department || '—'}
        </div>

        <div className="cabinet-stack" style={{ marginBottom: 14 }}>
          <div>
            <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 800, color: '#8faebb' }}>Дисциплина</p>
            <div className="cabinet-filters">
              {disciplines.map((d) => (
                <button
                  key={d.code}
                  type="button"
                  onClick={() => onSelectDiscipline(d.code)}
                  className={`cabinet-filter${selectedDisciplineCode === d.code ? ' active' : ''}`}
                >
                  {d.code} · {d.title}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 800, color: '#8faebb' }}>Группа</p>
            <div className="cabinet-filters">
              {groups.map((g) => (
                <button
                  key={g.group_code}
                  type="button"
                  onClick={() => onSelectGroup(g.group_code)}
                  className={`cabinet-filter${selectedGroupCode === g.group_code ? ' active' : ''}`}
                >
                  {g.group_code}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="cabinet-grid3" style={{ marginBottom: 14 }}>
          <article className="cabinet-card" style={{ ['--accent' as string]: '#e2ad3a' }}>
            <span className="cabinet-badge" style={{ '--badge': '#e2ad3a' } as CSSProperties}>
              7 неделя
            </span>
            <strong style={{ display: 'block', marginTop: 8 }}>Рубежный контроль 1</strong>
            <p style={{ margin: '6px 0 0', fontSize: 12 }}>Запланировано</p>
          </article>
          <article className="cabinet-card" style={{ ['--accent' as string]: '#9e80ff' }}>
            <span className="cabinet-badge" style={{ '--badge': '#9e80ff' } as CSSProperties}>
              15 неделя
            </span>
            <strong style={{ display: 'block', marginTop: 8 }}>Рубежный контроль 2</strong>
            <p style={{ margin: '6px 0 0', fontSize: 12 }}>Запланировано</p>
          </article>
          <article className="cabinet-card" style={{ ['--accent' as string]: '#49ddb7' }}>
            <span className="cabinet-badge" style={{ '--badge': '#49ddb7' } as CSSProperties}>
              после учебных недель
            </span>
            <strong style={{ display: 'block', marginTop: 8 }}>Итоговый контроль / экзамен</strong>
            <p style={{ margin: '6px 0 0', fontSize: 12 }}>Запланировано</p>
          </article>
        </div>

        <section className="cabinet-panel">
          <div className="cabinet-panel-body">
            <div className="cabinet-actions" style={{ marginBottom: 10, alignItems: 'center' }}>
              <span className="cabinet-badge" style={{ '--badge': '#41d2ff' } as CSSProperties}>
                {currentDiscipline?.title} · {selectedGroupCode}
              </span>
              <strong>
                {activeMode === 'scores' ? 'Баллы по календарю дисциплины' : 'Отметить посещаемость'}
              </strong>
            </div>

            <div className="cabinet-filters" style={{ marginBottom: 12 }}>
              <button
                type="button"
                className={`cabinet-filter${activeMode === 'scores' ? ' active' : ''}`}
                onClick={() => setActiveMode('scores')}
              >
                Баллы по неделям
              </button>
              <button
                type="button"
                className={`cabinet-filter${activeMode === 'attendance' ? ' active' : ''}`}
                onClick={() => setActiveMode('attendance')}
              >
                Посещаемость
              </button>
            </div>

            {activeMode === 'attendance' ? (
              <div className="cabinet-filters" style={{ marginBottom: 12 }}>
                {Array.from({ length: currentDiscipline?.calendar_json?.weeks || 15 }, (_, i) => i + 1).map((w) => (
                  <button
                    key={w}
                    type="button"
                    className={`cabinet-filter${selectedWeek === w ? ' active' : ''}`}
                    onClick={() => setSelectedWeek(w)}
                  >
                    Неделя {w}
                  </button>
                ))}
              </div>
            ) : null}

            {activeMode === 'scores' ? (
              <div style={{ overflowX: 'auto' }}>
                <table className="cabinet-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th rowSpan={2} style={{ textAlign: 'left', padding: 8 }}>
                        Обучающийся
                      </th>
                      {Array.from({ length: currentDiscipline?.calendar_json?.weeks || 15 }, (_, i) => i + 1).map((w) => (
                        <th key={w} colSpan={3} style={{ textAlign: 'center', padding: 8 }}>
                          Неделя {w}
                        </th>
                      ))}
                    </tr>
                    <tr>
                      {Array.from({ length: currentDiscipline?.calendar_json?.weeks || 15 }, (_, i) => i + 1).map((w) => (
                        <Fragment key={w}>
                          <th style={{ textAlign: 'center', padding: 4 }}>Лек.</th>
                          <th style={{ textAlign: 'center', padding: 4 }}>Практ.</th>
                          <th style={{ textAlign: 'center', padding: 4 }}>СРО</th>
                        </Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((st) => (
                      <tr key={st.id}>
                        <td style={{ padding: 8 }}>
                          <strong>{st.full_name}</strong>
                          <div style={{ fontSize: 10, color: '#8faebb' }}>{selectedGroupCode}</div>
                        </td>
                        {Array.from({ length: currentDiscipline?.calendar_json?.weeks || 15 }, (_, i) => i + 1).map((w) => (
                          <Fragment key={w}>
                            <td style={{ textAlign: 'center', padding: 4 }}>
                              <input
                                type="number"
                                min={0}
                                max={100}
                                className="cabinet-input"
                                style={{ width: 48, textAlign: 'center', padding: '4px 6px' }}
                                value={getScoreValue(st.user_id || st.full_name, w, 'lecture_score')}
                                onChange={(e) =>
                                  handleGradeCellChange(st.user_id || st.full_name, w, 'lecture_score', Number(e.target.value))
                                }
                              />
                            </td>
                            <td style={{ textAlign: 'center', padding: 4 }}>
                              <input
                                type="number"
                                min={0}
                                max={100}
                                className="cabinet-input"
                                style={{ width: 48, textAlign: 'center', padding: '4px 6px' }}
                                value={getScoreValue(st.user_id || st.full_name, w, 'practice_score')}
                                onChange={(e) =>
                                  handleGradeCellChange(st.user_id || st.full_name, w, 'practice_score', Number(e.target.value))
                                }
                              />
                            </td>
                            <td style={{ textAlign: 'center', padding: 4 }}>
                              <input
                                type="number"
                                min={0}
                                max={100}
                                className="cabinet-input"
                                style={{ width: 48, textAlign: 'center', padding: '4px 6px' }}
                                value={getScoreValue(st.user_id || st.full_name, w, 'sro_score')}
                                onChange={(e) =>
                                  handleGradeCellChange(st.user_id || st.full_name, w, 'sro_score', Number(e.target.value))
                                }
                              />
                            </td>
                          </Fragment>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="cabinet-stack">
                {students.map((st) => {
                  const lecStatus = getAttendanceStatus(st.user_id || st.full_name, 'LECTURE');
                  const pracStatus = getAttendanceStatus(st.user_id || st.full_name, 'PRACTICE');
                  return (
                    <article key={st.id} className="cabinet-card" style={{ ['--accent' as string]: '#41d2ff' }}>
                      <div className="cabinet-actions" style={{ alignItems: 'center', marginBottom: 8 }}>
                        <div>
                          <strong>{st.full_name}</strong>
                          <p style={{ margin: '4px 0 0', fontSize: 11, color: '#8faebb' }}>
                            {selectedGroupCode} · {st.attendance_rate}%
                          </p>
                        </div>
                        <span className="cabinet-spacer" />
                        <button
                          type="button"
                          className="cabinet-btn"
                          onClick={() => setSelectedStudentForModal(st)}
                        >
                          Карточка
                        </button>
                      </div>
                      <div className="cabinet-actions">
                        <button
                          type="button"
                          className="cabinet-btn"
                          onClick={() => handleAttendanceClick(st.user_id || st.full_name, 'LECTURE')}
                        >
                          {statusIcon(lecStatus)}
                          <span>Лекция · </span>
                          <span>{lecStatus}</span>
                        </button>
                        <button
                          type="button"
                          className="cabinet-btn"
                          onClick={() => handleAttendanceClick(st.user_id || st.full_name, 'PRACTICE')}
                        >
                          {statusIcon(pracStatus)}
                          <span>Практика · </span>
                          <span>{pracStatus}</span>
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </div>

      {selectedStudentForModal ? (
        <div className="cabinet-modal-backdrop" role="dialog" aria-modal="true">
          <div className="cabinet-modal">
            <div className="cabinet-modal-head">
              <div>
                <h2>Карточка обучающегося</h2>
                <p>{selectedStudentForModal.full_name}</p>
              </div>
              <button
                type="button"
                className="cabinet-btn"
                onClick={() => setSelectedStudentForModal(null)}
                aria-label="Закрыть"
              >
                ✕
              </button>
            </div>
            <div className="cabinet-modal-body">
              <div className="cabinet-grid2">
                <article className="cabinet-card">
                  <strong>Шифр</strong>
                  <p style={{ margin: '4px 0 0' }}>{selectedStudentForModal.student_code}</p>
                </article>
                <article className="cabinet-card">
                  <strong>Курс</strong>
                  <p style={{ margin: '4px 0 0' }}>{selectedStudentForModal.course_year} курс</p>
                </article>
              </div>
              <div className="cabinet-note" style={{ marginTop: 12 }}>
                {selectedStudentForModal.program_name}
              </div>
              <div className="cabinet-grid3" style={{ marginTop: 12 }}>
                <article className="cabinet-card">
                  <strong>{selectedStudentForModal.attendance_rate}%</strong>
                  <p style={{ margin: '4px 0 0', fontSize: 12 }}>Посещаемость</p>
                </article>
                <article className="cabinet-card">
                  <strong>{selectedStudentForModal.current_score}</strong>
                  <p style={{ margin: '4px 0 0', fontSize: 12 }}>Текущий балл</p>
                </article>
                <article className="cabinet-card">
                  <strong>{selectedStudentForModal.overdue_count}</strong>
                  <p style={{ margin: '4px 0 0', fontSize: 12 }}>Просрочки</p>
                </article>
              </div>
              <div className="cabinet-actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
                <button type="button" className="cabinet-btn" onClick={() => setSelectedStudentForModal(null)}>
                  Закрыть
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
